import { realApiClient, mapApiListing, mapApiListingSeller, type ApiListingPayload } from "./apiClient";
import { listingFilterSchema, type ListingFilters } from "../schemas/listingSchemas";
import type { Listing, Pharmacy } from "../types/domain";

let listingCache: Listing[] = [
  { id: "lst-ozempic", ndc: "00169-4132-12", name: "Ozempic", dosage: "2mg/1.5mL Pen", lot: "MK-29A", unitPrice: 1850, quantity: 2, expiresAt: "2027-04-01", shipsInHours: 24, sellerId: "pharm-olympic", packetStatus: "ready", status: "active", tags: ["shortage", "brand", "cold-chain"] },
  { id: "lst-stelara", ndc: "57894-0060-03", name: "Stelara", dosage: "45mg Syringe", lot: "ST-81Q", unitPrice: 4620, quantity: 3, expiresAt: "2027-01-01", shipsInHours: 24, sellerId: "pharm-maple", packetStatus: "ready", status: "active", tags: ["brand", "specialty"] },
  { id: "lst-trulicity", ndc: "00002-1434-80", name: "Trulicity", dosage: "1.5mg Pen", lot: "TR-17L", unitPrice: 820, quantity: 4, expiresAt: "2026-11-01", shipsInHours: 24, sellerId: "pharm-green-valley", packetStatus: "needs_review", status: "needs_packet_review", tags: ["shortage"] },
];
let pharmacyCache: Pharmacy[] = [
  { id: "pharm-green-valley", name: "Green Valley Rx", city: "Seattle", state: "WA", licenseStatus: "verified", rating: 4.9 },
  { id: "pharm-olympic", name: "Olympic Pharmacy", city: "Tacoma", state: "WA", licenseStatus: "verified", rating: 4.8 },
  { id: "pharm-maple", name: "Maple Care Rx", city: "Portland", state: "OR", licenseStatus: "verified", rating: 4.7 },
];

function uniquePharmacies(apiListings: ApiListingPayload[]): Pharmacy[] {
  const byId = new Map<string, Pharmacy>();
  for (const apiListing of apiListings) {
    const pharmacy = mapApiListingSeller(apiListing);
    byId.set(pharmacy.id, pharmacy);
  }
  return [...byId.values()];
}

function applyFilters(listings: Listing[], filters: ListingFilters = {}): Listing[] {
  const parsed = listingFilterSchema.parse(filters);
  return listings.filter(listing => {
    if (parsed.packetReady && listing.packetStatus !== "ready") return false;
    if (parsed.shortageOnly && !listing.tags.includes("shortage")) return false;
    if (parsed.brandOnly && !listing.tags.includes("brand")) return false;
    if (parsed.shipsWithinHours && listing.shipsInHours > parsed.shipsWithinHours) return false;
    if (parsed.query) {
      const haystack = [listing.ndc, listing.name, listing.dosage, listing.lot, ...listing.tags].join(" ").toLowerCase();
      if (!haystack.includes(parsed.query.toLowerCase())) return false;
    }
    return true;
  });
}

export const marketplaceService = {
  listListings: (filters: ListingFilters = {}) => applyFilters(listingCache, filters),
  getListing: (id: string) => listingCache.find(listing => listing.id === id) ?? listingCache[0],
  listPharmacies: () => pharmacyCache,
  getPharmacy: (id: string) => pharmacyCache.find(pharmacy => pharmacy.id === id),
  async refreshListings(filters: ListingFilters = {}) {
    const apiListings = await realApiClient.listListings();
    listingCache = apiListings.map(mapApiListing);
    pharmacyCache = uniquePharmacies(apiListings);
    return applyFilters(listingCache, filters);
  },
  async fetchListing(id: string) {
    const apiListing = await realApiClient.getListing(id);
    if (!apiListing) return undefined;
    const listing = mapApiListing(apiListing);
    listingCache = [listing, ...listingCache.filter(item => item.id !== listing.id)];
    const pharmacy = mapApiListingSeller(apiListing);
    pharmacyCache = [pharmacy, ...pharmacyCache.filter(item => item.id !== pharmacy.id)];
    return listing;
  },
};
